import React, {
  CSSProperties,
  ChangeEventHandler,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Divider,
  FormInstance,
  Input,
  Space,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';

import Text from 'antd/lib/typography/Text';
import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';
import { getUploadModels } from '@components/Actions/ActionsUpload/UploadModels';
import CustomModalInfo from '@components/CustomModalInfo';
import CustomTag from '@components/CustomTag';

const { TextArea } = Input;

const useManualImportStep = (
  actionKey: number | undefined,
  form: FormInstance<any> | undefined,
  isLoraWan?: boolean,
): ReactNode => {
  const { t } = useTranslation(['actions-modal', 'attributes']);
  const { motOperators, isMot, theme } = useContext(SessionContext);
  const [openModalInfo, setOpenModalInfo] = useState(false);

  const importModel = useMemo((): {
    fields: string[];
    fieldsCount: number;
    placeholder: string;
    selectionModel: string;
  } => {
    const uploadModels = getUploadModels({
      actionKey,
      t,
      isLoraWan,
    });
    const modelsData = uploadModels?.data;
    const fields = uploadModels?.columns?.map(col => col.dataIndex);
    const fieldsCount = fields?.length;

    let placeholder = '';
    let selectionModel = '';

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    modelsData.forEach(({ key, ...restItem }, index) => {
      placeholder += `${Object.values(restItem).join(';')}\n`;

      const isFirstIteration = index === 0;
      if (isFirstIteration) {
        selectionModel += `${Object.keys(restItem)
          ?.map((keyItem: string) => t(`attributes:${keyItem}`))
          .join(';')}\n`;
      }
    });

    return {
      fields,
      fieldsCount,
      placeholder,
      selectionModel,
    };
  }, [actionKey, isLoraWan, t]);

  const treatValue = useCallback(
    (value: string) => {
      if (value === '') {
        return undefined;
      }
      const values = value?.split('\n')?.map(str => {
        const strSplit = str?.split(';')?.filter(val => val !== '');
        const notHaveImsi = strSplit?.length === 3;
        if (notHaveImsi) {
          return [...strSplit, undefined];
        }
        return strSplit;
      });

      const fields = importModel?.fields;

      return values.reduce((acc: any, crr) => {
        // It initializes a new object for each subarray in 'VALUES'
        const newDevice: { [key: string]: string | undefined } = {};

        // Iterate on the corresponding values and fields, assigning them to the new object
        crr.forEach((val, index) => {
          newDevice[fields[index]] = val;
        });

        // Adds the new object to the result
        acc.push(newDevice);

        return acc;
      }, []);
    },
    [importModel],
  );

  const handleOnChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
    ({ target: { value } }) => {
      const devices = treatValue(value);
      form?.setFieldValue('uploadedItems', devices);
    },
    [form, treatValue],
  );

  const descriptionTag = useCallback(
    (
      type: 'semicolon' | 'more-than-one' | 'all-required-except-imsi',
      style?: CSSProperties,
    ) => {
      const descriptionText = t(`simcard-import.manually.help.${type}`);
      const model = importModel?.selectionModel;
      const isAllRequiredExceptImsi = type === 'all-required-except-imsi';

      return (
        <>
          <p style={{ fontSize: theme?.fontSizes?.small, ...style }}>
            {isAllRequiredExceptImsi && isMot ? (
              <Alert
                type="error"
                style={{
                  fontSize: theme?.fontSizes?.small,
                  color: theme?.colors?.text,
                }}
                message={
                  <>
                    <InfoCircleOutlined
                      style={{ marginRight: 6, color: theme?.colors?.primary }}
                    />
                    <span style={{ color: theme?.colors?.text1 }}>
                      {descriptionText}
                    </span>
                  </>
                }
              />
            ) : (
              descriptionText
            )}
          </p>
          {!isAllRequiredExceptImsi && (
            <CustomTag
              style={{
                width: '100%',
                padding: '8px 16px',
                borderStyle: 'dashed',
                fontWeight: 'normal',
                color: 'var(--primary-color)',
                fontSize: theme?.fontSizes?.default,
              }}
            >
              <span>{model}</span>
              <br />
              {type === 'more-than-one' && <span>{model}</span>}
            </CustomTag>
          )}
        </>
      );
    },
    [importModel?.selectionModel, isMot, t, theme],
  );

  const carriersAvailable = useMemo(
    () =>
      isMot && (
        <>
          <Divider
            style={{ fontSize: '0.825rem', opacity: 0.8, marginTop: 24 }}
          >
            {t('simcard-import.carriers-available')}
          </Divider>
          {motOperators?.map(operator => (
            <CustomTag key={operator} style={{ margin: 4, opacity: 0.8 }}>
              {operator}
            </CustomTag>
          ))}
        </>
      ),
    [isMot, motOperators, t],
  );

  return (
    <div>
      <CustomModalInfo
        title={t('simcard-import.manually.selection-model')}
        open={openModalInfo}
        onClose={() => setOpenModalInfo(false)}
      >
        <div>
          {importModel?.fieldsCount > 1 &&
            descriptionTag('semicolon', { marginBottom: 16 })}
          {descriptionTag('more-than-one')}
          {isMot &&
            !isLoraWan &&
            descriptionTag('all-required-except-imsi', { marginTop: 16 })}
          {!isLoraWan && carriersAvailable}
        </div>
      </CustomModalInfo>
      <Space style={{ marginBottom: 8 }}>
        <Text>
          {t('simcard-import.manually.specific-selection')}
          <Tooltip title={t('simcard-import.manually.selection-model')}>
            <Button
              type="link"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={() => setOpenModalInfo(true)}
            />
          </Tooltip>
        </Text>
      </Space>
      <TextArea
        autoSize
        style={{ minHeight: 120, maxHeight: 160 }}
        onChange={handleOnChange}
        placeholder={importModel?.placeholder}
      />
    </div>
  );
};

export default useManualImportStep;
